import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getStripeConnectStatus, startStripeOnboarding } from '../api/client';
import type { StripeConnectStatus } from '../types';
import { CheckCircle, AlertCircle, Loader2, CreditCard, ArrowRight } from 'lucide-react';

export function StripeConnectReturnPage() {
  useDocumentTitle('Stripe Setup');
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // Check if this is the refresh page (link expired)
  const isRefresh = location.pathname.includes('/refresh');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getStripeConnectStatus();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueOnboarding = async () => {
    try {
      setIsActionLoading(true);
      setError(null);
      const { onboardingUrl } = await startStripeOnboarding();
      window.location.href = onboardingUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to continue onboarding');
      setIsActionLoading(false);
    }
  };

  const handleGoToSettings = () => {
    navigate('/sell/settings');
  };

  const handleGoToSellerHub = () => {
    navigate('/sell');
  };

  if (isLoading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.loadingCard}>
              <Loader2 size={48} color={colors.brand.blue} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={styles.loadingText}>Checking your payment setup status...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.card}>
              <div style={styles.iconContainer}>
                <div style={{ ...styles.iconCircle, backgroundColor: '#FEE2E2' }}>
                  <AlertCircle size={32} color="#DC2626" />
                </div>
              </div>
              <h1 style={styles.title}>Something went wrong</h1>
              <p style={styles.description}>{error}</p>
              <button style={styles.primaryButton} onClick={handleGoToSettings}>
                Go to Settings
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Refresh page - link expired
  if (isRefresh) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.card}>
              <div style={styles.iconContainer}>
                <div style={{ ...styles.iconCircle, backgroundColor: '#FEF3C7' }}>
                  <AlertCircle size={32} color="#D97706" />
                </div>
              </div>
              <h1 style={styles.title}>Link Expired</h1>
              <p style={styles.description}>
                Your onboarding link has expired. This happens for security reasons.
                Click below to get a new link and continue where you left off.
              </p>
              <button
                style={styles.primaryButton}
                onClick={handleContinueOnboarding}
                disabled={isActionLoading}
              >
                {isActionLoading ? (
                  <>
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Loading...
                  </>
                ) : (
                  <>
                    Continue Setup
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Onboarding complete
  if (status?.status === 'COMPLETE' && status?.chargesEnabled) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.card}>
              <div style={styles.iconContainer}>
                <div style={{ ...styles.iconCircle, backgroundColor: '#D1FAE5' }}>
                  <CheckCircle size={32} color="#059669" />
                </div>
              </div>
              <h1 style={styles.title}>Payment Setup Complete</h1>
              <p style={styles.description}>
                Your account is ready to accept payments. You can now start a live show
                and receive payments from your sales.
              </p>
              <div style={styles.statusDetails}>
                <div style={styles.statusItem}>
                  <span style={styles.statusLabel}>Accept Payments</span>
                  <span style={styles.statusYes}>Ready</span>
                </div>
                <div style={styles.statusItem}>
                  <span style={styles.statusLabel}>Receive Payouts</span>
                  <span style={status.payoutsEnabled ? styles.statusYes : styles.statusPending}>
                    {status.payoutsEnabled ? 'Ready' : 'Pending'}
                  </span>
                </div>
              </div>
              <button style={styles.primaryButton} onClick={handleGoToSellerHub}>
                Go to Seller Hub
                <ArrowRight size={18} />
              </button>
              <button style={styles.secondaryButton} onClick={handleGoToSettings}>
                View Settings
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Onboarding incomplete
  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.iconContainer}>
              <div style={{ ...styles.iconCircle, backgroundColor: colors.brand.blue15 }}>
                <CreditCard size={32} color={colors.brand.blue} />
              </div>
            </div>
            <h1 style={styles.title}>Almost There</h1>
            <p style={styles.description}>
              Your payment setup is not complete yet. Please finish the remaining steps
              to start accepting payments.
            </p>
            <div style={styles.statusDetails}>
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>Details Submitted</span>
                <span style={status?.detailsSubmitted ? styles.statusYes : styles.statusNo}>
                  {status?.detailsSubmitted ? 'Yes' : 'No'}
                </span>
              </div>
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>Accept Payments</span>
                <span style={status?.chargesEnabled ? styles.statusYes : styles.statusNo}>
                  {status?.chargesEnabled ? 'Ready' : 'Not Ready'}
                </span>
              </div>
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>Receive Payouts</span>
                <span style={status?.payoutsEnabled ? styles.statusYes : styles.statusNo}>
                  {status?.payoutsEnabled ? 'Ready' : 'Not Ready'}
                </span>
              </div>
            </div>
            <button
              style={styles.primaryButton}
              onClick={handleContinueOnboarding}
              disabled={isActionLoading}
            >
              {isActionLoading ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Loading...
                </>
              ) : (
                <>
                  Continue Setup
                  <ArrowRight size={18} />
                </>
              )}
            </button>
            <button style={styles.secondaryButton} onClick={handleGoToSettings}>
              Go to Settings
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    minHeight: 'calc(100vh - 64px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    width: '100%',
    maxWidth: '480px',
  },
  loadingCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '48px 24px',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
  },
  loadingText: {
    fontSize: '16px',
    color: colors.gray[600],
    margin: 0,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 24px',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    textAlign: 'center',
  },
  iconContainer: {
    marginBottom: '24px',
  },
  iconCircle: {
    width: '72px',
    height: '72px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 12px 0',
  },
  description: {
    fontSize: '15px',
    color: colors.gray[600],
    lineHeight: 1.6,
    margin: '0 0 24px 0',
  },
  statusDetails: {
    width: '100%',
    padding: '16px',
    backgroundColor: colors.gray[100],
    borderRadius: '12px',
    marginBottom: '24px',
  },
  statusItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  statusLabel: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  statusYes: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#059669',
  },
  statusNo: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#DC2626',
  },
  statusPending: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#D97706',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};
